import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cronHealthRedis, cronHealthDb } from '../redux/reducers/utils/commonReducer';
import { notification } from 'antd';
import { checkAccess } from '../core/utils';

export const useCronHealthCheck = () => {
  const dispatch = useDispatch();
  const { accessComponentsForUser } = useSelector((store) => store.services);
  const isAdmin = checkAccess(accessComponentsForUser, 'moderation');
  const [api, contextHolder] = notification.useNotification();

  const showPersistentNotification = useCallback((type, message, description) => {
    const key = `cron-health-${Date.now()}`;
    
    const config = {
      message,
      description,
      placement: 'topRight',
      duration: 0,
      key,
    };

    switch (type) {
      case 'error':
        api.error(config);
        break;
      case 'warning':
        api.warning(config);
        break;
      default:
        api.info(config);
    }
  }, [api]);

  const checkTimeDifference = (lastExec, currentTime) => {
    const lastExecDate = new Date(lastExec).getTime();
    const currentDate = new Date(currentTime).getTime();
    const diffMinutes = (currentDate - lastExecDate) / (1000 * 60);
    return Math.round(diffMinutes);
  };

  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    const checkCronHealth = async () => {
      try {
        // Вызываем оба метода проверки крона
        const [redisResponse, dbResponse] = await Promise.all([
          dispatch(cronHealthRedis()).unwrap(),
          dispatch(cronHealthDb()).unwrap()
        ]);

        console.log('Cron Redis:', `lastExecution: ${redisResponse.data.lastExecution}`, `currentTime: ${redisResponse.data.currentTime}`);
        console.log('Cron DB:', `lastExecution: ${dbResponse.data.lastExecution}`, `currentTime: ${dbResponse.data.currentTime}`);

        // Проверяем здоровье Redis крона
        const isRedisHealthy = redisResponse.result &&
          checkTimeDifference(
            redisResponse.data.lastExecution, 
            redisResponse.data.currentTime
          ) <= 6;

        // Проверяем здоровье DB крона
        const isDbHealthy = dbResponse.result &&
          checkTimeDifference(
            dbResponse.data.lastExecution, 
            dbResponse.data.currentTime
          ) <= 6;

        // Обрабатываем ошибки для Redis
        if (!isRedisHealthy || !redisResponse.result) {
          const timeDiff = checkTimeDifference(
            redisResponse.data.lastExecution, 
            redisResponse.data.currentTime
          );
          
          showPersistentNotification(
            'error',
            'Внимание! Критическая ошибка.',
            `Не работает Redis КРОН. Последний запуск: ${timeDiff} мин. назад. Связаться с разработчиками!`
          );
        }

        // Обрабатываем ошибки для DB
        if (!isDbHealthy || !dbResponse.result) {
          const timeDiff = checkTimeDifference(
            dbResponse.data.lastExecution, 
            dbResponse.data.currentTime
          );
          
          showPersistentNotification(
            'error',
            'Внимание! Критическая ошибка.',
            `Не работает DB КРОН. Последний запуск: ${timeDiff} мин. назад. Связаться с разработчиками!`
          );
        }

      } catch (error) {
        console.error('Ошибка при проверке состояния Cron:', error);
        
        // Определяем тип ошибки
        const isNetworkError = (
          error.code === 'NETWORK_ERROR' || 
          error.message?.includes('Network Error') ||
          error.message?.includes('timeout') ||
          (error.name === 'AxiosError' && !error.response) ||
          !navigator.onLine
        );
        
        if (isNetworkError) {
          // Проблема с сетью
          showPersistentNotification(
            'warning',
            'Проблема с подключением',
            `[NETWORK_ERROR] Проблем с интернет-соединением. Проверьте подключение к сети и попробуйте снова.`
          );
        } else if (error.response?.status >= 500) {
          // Серверная ошибка
          showPersistentNotification(
            'error',
            'Ошибка сервера',
            `[${error.response?.status}] Ошибки сервера. Обратитесь к разработчикам!`
          );
        } else if (error.response?.status === 522) {
          // Connection Timed Out (Cloudflare)
          showPersistentNotification(
            'error',
            'Ошибка подключения',
            '[522] Время подключения к серверу истекло. Обратитесь к разработчикам!'
          );
        } else if (error.response?.status === 504) {
          // Gateway Timeout
          showPersistentNotification(
            'error',
            'Ошибка шлюза',
            '[504] Время ожидания ответа от сервера истекло. Обратитесь к разработчикам!'
          );
        } else if (error.response?.status === 404) {
          // Endpoint не найден
          showPersistentNotification(
            'warning',
            'Сервис недоступен',
            '[404] Эндпоинт проверки КРОН недоступен. Обратитесь к разработчикам!'
          );
        } else {
          // Другие ошибки
          const errorDetails = {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            code: error.code,
            name: error.name,
            stack: error.stack
          };
          
          showPersistentNotification(
            'warning',
            'Неизвестная ошибка',
            `[${error.response?.status || 'NO_STATUS'}] Неизвестная ошибка. Детали: ${JSON.stringify(errorDetails, null, 2)}`
          );
        }
      }
    };

    // Выполняем проверку сразу при монтировании
    checkCronHealth();
    
    // Устанавливаем интервал для периодической проверки
    const intervalId = setInterval(checkCronHealth, 5 * 60 * 1000); // 5 минут

    // Очищаем интервал при размонтировании компонента
    return () => clearInterval(intervalId);
  }, [dispatch, isAdmin, showPersistentNotification]);

  return contextHolder;
};
